import { createApp } from 'vue'
import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import * as Sentry from '@sentry/vue'
import './styles/tailwind.css'
import './styles/main.scss'
import App from './App.vue'
import router from './router'
import { DEFAULT_QUERY_OPTIONS } from './config/query'
import { configApi, healthApi } from '@/api'
import { bootstrapEruda } from '@/dev/bootstrapEruda'
import { isDiagShippingEnabled } from '@/dev/isDiagShippingEnabled'
import { flushDiagTrace } from '@/services/diag/diag-shipper'
import { diagLog } from '@/services/diag/diag-trace'
import { diagnosticContext, diagnosticEnvironment } from '@/services/diag/context'
import { initializeSpinDiagnostics } from '@/services/diag/spin-diagnostics'
import { installCrosschainBuyFeature } from '@/features/crosschain-buy'

const app = createApp(App)
const devtoolsReady = bootstrapEruda()

// Initialize Sentry
const sentryApiAllowlist = [
  import.meta.env.VITE_API_BASE_URL,
  typeof window !== 'undefined' ? window.location.origin : undefined,
].filter((u): u is string => typeof u === 'string' && u.length > 0)

Sentry.init({
  app,
  dsn: 'https://fec2cd44bca1aae318a79d07c21ec4cf@o4510747821670400.ingest.us.sentry.io/4510770361139200',
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    // Aggressively mask DOM in Session Replay: wallet addresses, swap amounts,
    // and transaction hashes are rendered as plain text in the UI. With
    // sendDefaultPii enabled these would otherwise be captured verbatim.
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: false,
      networkDetailAllowUrls: sentryApiAllowlist,
      networkCaptureBodies: false,
      networkRequestHeaders: [],
      networkResponseHeaders: [],
    }),
  ],
  // Performance Monitoring - capture 20% of transactions in production
  tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
  // Session Replay - capture 10% of sessions, 100% on errors
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Collect technical HTTP context, including User-Agent. Auth/signing payloads stay excluded.
  dataCollection: {
    userInfo: true,
    cookies: false,
    httpHeaders: {
      deny: [
        'authorization',
        'proxy-authorization',
        'cookie',
        'set-cookie',
        'x-api-key',
        'x-diag-token',
      ],
    },
    httpBodies: [],
    urlQueryParams: false,
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    graphQL: { document: false, variables: false },
  },
  // Environment
  environment: diagnosticEnvironment(),
  release: import.meta.env.VITE_RELEASE_SHA,
})
Sentry.setTag('diag_session_id', diagnosticContext().sessionId)
Sentry.setContext('client_diagnostics', diagnosticContext())
initializeSpinDiagnostics()

// Best-effort navigation flush. Native process termination may never fire pagehide;
// spin checkpoints also ship immediately while the page is alive.
if (typeof window !== 'undefined') {
  const diagToken = import.meta.env.VITE_DIAG_INGEST_TOKEN
  const releaseSha = import.meta.env.VITE_RELEASE_SHA
  window.addEventListener('pagehide', () => {
    if (!isDiagShippingEnabled()) return
    void flushDiagTrace({ token: diagToken, releaseSha })
  })

  // Retry retained batches with shipper backoff; no automatic spin retries.
  if (isDiagShippingEnabled()) {
    diagLog('diagnostics.started', diagnosticContext())
    void flushDiagTrace({ token: diagToken, releaseSha })
    setInterval(() => {
      void flushDiagTrace({ token: diagToken, releaseSha })
    }, 5000)
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: DEFAULT_QUERY_OPTIONS,
  },
})
app.use(router)
app.use(VueQueryPlugin, { queryClient })

// Perform maintenance and health checks before mounting the app
async function initializeApp(): Promise<void> {
  await devtoolsReady

  try {
    // 1) Check maintenance feature flag first (highest priority)
    const maintenanceFlag = await configApi.getFlag('maintenance')
    const isMaintenance =
      maintenanceFlag.success && maintenanceFlag.data ? Boolean(maintenanceFlag.data.value) : false

    if (isMaintenance) {
      await router.push('/maintenance')
    } else {
      // 2) If not in maintenance mode, perform health check
      const healthCheck = await healthApi.check()
      if (!healthCheck.success) {
        // Navigate to maintenance page before mounting
        await router.push('/maintenance')
      }
    }
  } catch (error) {
    // Network error or other failure - redirect to maintenance
    console.error('Initialization checks failed:', error)
    await router.push('/maintenance')
  }

  // Initialize Reown AppKit before mounting so wallet flows are ready on first UI render.
  try {
    await installCrosschainBuyFeature()
  } catch (error) {
    console.error('AppKit init failed:', error)
  }

  // Mount the app regardless (maintenance page will handle its own polling)
  app.mount('#app')
}

void initializeApp()
